import { Icon, Stack, Typography } from '@mui/material';
import {
  AnalyticsEventName,
  AttendanceRecordSource,
  formatAttendanceSessionForTracking,
  trackAmplitudeEvent,
} from '@schooly/analytics';
import {
  AttendanceCode,
  AttendanceSessionEnum,
  DATE_TIME_FORMAT_FNS,
  JustificationBase,
  JustificationSave,
} from '@schooly/api';
import { useAuth } from '@schooly/components/authentication';
import { AttendanceCodeCircle } from '@schooly/components/attendance';
import { AttendanceCodeSelect, AttendanceCodeSelectOption } from '@schooly/components/filters';
import { NotePlusIcon, SimpleButton, StaffIcon, TypographyWithOverflowHint } from '@schooly/style';
import { getUserFullName } from '@schooly/utils/user-helpers';
import { format } from 'date-fns';
import { FC, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form-lts';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';

import { allowRenderGroups, allowRenderStaff } from '../../../../helpers/renderRoutesByPermissions';
import { useAttendanceNotificationsEnabled } from '../../../../hooks/useAttendanceNotificationsEnabled';
import { ViewGroupModalTabs } from '../../../Groups/utils';
import { hasNotification } from '../../attendance-utils';
import { useJustificationCodeChange } from '../../useJustificationCodeChange';
import { AttendanceGridStudent } from '../common/types';
import { AttendanceSidebarInternalNoteInput } from './AttendanceSidebarInternalNoteInput';
import { AttendanceSidebarNotTakenWarning } from './AttendanceSidebarWarning';
import {
  AttendanceFormEntries,
  getEntryGroupKey,
  getFormEntryIndex,
  getSessionEntries,
  getTakeAttendanceLink,
  getTrackingPointLabel,
  isPeriodOnlyEntry,
  SidebarEntry,
} from './utils';

type AttendanceSidebarEntryContentProps = {
  entry: SidebarEntry;
  student: AttendanceGridStudent;
  justification: JustificationBase | null;
  codesById: Record<string, AttendanceCode>;
  date: string;
  source: AttendanceRecordSource;
  disabled?: boolean;
};

export const AttendanceSidebarEntryContent: FC<AttendanceSidebarEntryContentProps> = ({
  entry,
  student,
  justification,
  codesById,
  date,
  source,
  disabled,
}) => {
  const { formatMessage } = useIntl();
  const { permissions } = useAuth();
  const canViewStaff = allowRenderStaff(permissions);
  const canViewGroups = allowRenderGroups(permissions);
  const notificationsEnabled = useAttendanceNotificationsEnabled();
  const { confirmCodeChange } = useJustificationCodeChange();
  const { watch, setValue } = useFormContext<AttendanceFormEntries>();

  const entries = watch('entries');
  const entryIndex = useMemo(() => getFormEntryIndex(entries, entry), [entries, entry]);
  const hasEntry = entryIndex >= 0;
  const attendanceCodeId = hasEntry ? entries[entryIndex]?.attendance_code_id : null;
  const hasAttendanceCode = !!attendanceCodeId;
  const comment = hasEntry ? entries[entryIndex]?.comment : null;
  const hasNote = !!comment?.trim();
  const sessionLabel = entry.session
    ? formatMessage({
        id: entry.session === AttendanceSessionEnum.Am ? 'attendance-AM' : 'attendance-PM',
      })
    : '';

  const hasInitNote = !!entry.comments[0]?.text?.trim();
  const [isNoteOpen, setIsNoteOpen] = useState(hasInitNote);

  const latestChange = entry.latest_change;
  const latestChangeGroup = latestChange?.updated_from_group ?? null;
  const showLatestChanges = Boolean(latestChange?.author || latestChangeGroup);

  const showNotificationIndicator = !isPeriodOnlyEntry(entry);
  const codeOptions = useMemo(
    () =>
      buildAttendanceCodeOptions(
        Object.values(codesById),
        notificationsEnabled,
        showNotificationIndicator,
      ),
    [codesById, notificationsEnabled, showNotificationIndicator],
  );

  const selectedOption = useMemo(() => {
    const noStatusOption = {
      id: '',
      label: formatMessage({ id: 'attendance-NoStatus' }),
      avatar: <AttendanceCodeCircle size={SELECT_CIRCLE_SIZE} />,
    };

    if (!hasAttendanceCode) return noStatusOption;

    return codeOptions.flat().find((option) => option.id === attendanceCodeId) ?? noStatusOption;
  }, [attendanceCodeId, codeOptions, formatMessage, hasAttendanceCode]);

  const applyCode = (codeId: string, note: JustificationSave | null) => {
    setValue(`entries.${entryIndex}.attendance_code_id`, codeId, { shouldDirty: true });
    setValue('justification', note ?? undefined, { shouldDirty: true });
  };

  const handleSelect = async (codeId: string) => {
    const code = codesById[codeId];
    if (!code) return;

    const wasNoStatus = !hasAttendanceCode;

    const result = await confirmCodeChange({
      newCode: code,
      codes: Object.values(codesById),
      entries: getSessionEntries(entries),
      sessions: entry.session ? [entry.session] : [],
      justification,
      pendingNote: watch('justification') ?? null,
      absenceNotice: entry.absence_request?.comment ?? null,
      relation: student,
      date,
    });
    if (!result) return;

    applyCode(codeId, result.note);

    const periodOrders = entry.periods.map((period) => period.lesson.order);
    const sessionValue = formatAttendanceSessionForTracking(entry.session, periodOrders);
    if (!sessionValue) return;

    if (wasNoStatus) {
      trackAmplitudeEvent({
        name: AnalyticsEventName.AttendanceTake,
        payload: { source, session: sessionValue },
      });
      return;
    }

    if (attendanceCodeId === codeId) return;

    trackAmplitudeEvent({
      name: AnalyticsEventName.AttendanceEditRecord,
      payload: { source, session: sessionValue },
    });
  };

  const renderEntryContent = () => {
    if (!hasAttendanceCode) {
      return entry.groups.length ? (
        entry.groups.map((group) => (
          <AttendanceSidebarNotTakenWarning
            key={getEntryGroupKey(group)}
            trackingPointLabel={getTrackingPointLabel(entry, sessionLabel, group)}
            link={disabled ? undefined : getTakeAttendanceLink(entry.session, group, date)}
            group={group}
          />
        ))
      ) : (
        <AttendanceSidebarNotTakenWarning trackingPointLabel={sessionLabel} />
      );
    }

    return (
      <>
        {showLatestChanges && (
          <Stack>
            <Typography color="text.secondary">
              {formatMessage({ id: 'attendance-LatestChanges' })}{' '}
              {format(new Date(entry.updated_at ?? entry.created_at), DATE_TIME_FORMAT_FNS)}
            </Typography>

            <Stack direction="row" alignItems="center" gap={0.5}>
              <Icon fontSize="small" sx={{ color: 'text.secondary' }}>
                <StaffIcon />
              </Icon>
              {latestChange.author && (
                <>
                  <Stack maxWidth="50%" overflow="hidden">
                    <TypographyWithOverflowHint
                      variant="h3"
                      color="text.primary"
                      noWrap
                      {...(canViewStaff
                        ? {
                            component: Link,
                            to: `/staff/${latestChange.author.relation_id}`,
                            target: '_blank',
                            sx: {
                              '&:hover': { textDecoration: 'underline', color: 'text.primary' },
                            },
                          }
                        : {})}
                    >
                      {getUserFullName(latestChange.author)}
                    </TypographyWithOverflowHint>
                  </Stack>
                  <Typography variant="h3" color="text.primary">
                    •
                  </Typography>
                </>
              )}

              <Stack maxWidth="50%" overflow="hidden">
                {latestChangeGroup ? (
                  <TypographyWithOverflowHint
                    variant="h3"
                    color="text.primary"
                    noWrap
                    {...(canViewGroups
                      ? {
                          component: Link,
                          to: `/groups/${latestChangeGroup.id}?tab=${ViewGroupModalTabs.Attendance}`,
                          target: '_blank',
                          sx: {
                            '&:hover': { textDecoration: 'underline', color: 'text.primary' },
                          },
                        }
                      : {})}
                  >
                    {latestChangeGroup.name}
                  </TypographyWithOverflowHint>
                ) : (
                  <TypographyWithOverflowHint variant="h3" color="text.primary" noWrap>
                    {formatMessage({ id: 'attendance-Individually' })}
                  </TypographyWithOverflowHint>
                )}
              </Stack>
            </Stack>
          </Stack>
        )}

        {hasEntry && (
          <Stack alignItems="flex-start" mt={1}>
            {isNoteOpen ? (
              <AttendanceSidebarInternalNoteInput
                name={`entries.${entryIndex}.comment`}
                disabled={disabled}
                autoFocus={!hasNote}
                onEmptyBlur={() => setIsNoteOpen(false)}
              />
            ) : (
              <SimpleButton
                inverse
                startIcon={<NotePlusIcon />}
                disabled={disabled}
                onClick={() => setIsNoteOpen(true)}
              >
                {formatMessage({ id: 'attendance-AddInternalNote' })}
              </SimpleButton>
            )}
          </Stack>
        )}
      </>
    );
  };

  return (
    <Stack gap={1}>
      <AttendanceCodeSelect
        options={codeOptions}
        selectedOption={selectedOption}
        disabled={disabled}
        withoutRightIcon={disabled}
        sx={
          disabled
            ? {
                borderColor: 'common.light2',
                bgcolor: 'transparent',
                color: 'primary.main',
              }
            : undefined
        }
        onSelect={({ id }) => {
          if (!hasEntry || disabled) return;
          handleSelect(id);
        }}
      />
      {renderEntryContent()}
    </Stack>
  );
};

const SELECT_CIRCLE_SIZE = 20;

const buildAttendanceCodeOptions = (
  codes: AttendanceCode[],
  notificationsEnabled: boolean,
  showNotificationIndicator: boolean,
): AttendanceCodeSelectOption[][] =>
  codes.reduce<AttendanceCodeSelectOption[][]>(
    (acc, code) => {
      acc[code.is_present ? 0 : 1].push({
        label: code.name,
        id: code.id,
        withNotification: showNotificationIndicator && hasNotification(code, notificationsEnabled),
        avatar: <AttendanceCodeCircle code={code} size={SELECT_CIRCLE_SIZE} />,
      });

      return acc;
    },
    [[], []],
  );
